import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  type CountryCode,
} from 'libphonenumber-js'

/**
 * Formatting a phone number the way its own country writes it, while it is being typed.
 *
 * Every country has its own shape — `+55 (11) 99999-1234`, `+1 (202) 555-0123`,
 * `+34 612 34 56 78` — and none of them is invented here: `libphonenumber-js` already
 * carries every one of them, and this only decides which country's shape to use and puts
 * the country code back in front of it.
 */

/**
 * Which country to format as, for a given country code.
 *
 * Several countries share a code — 25 of them share `+1` — but they also share the
 * formatting rules that come with it, so any of them produces the same shape. The first is
 * as good as the rest.
 */
const countryByCallingCode = new Map<string, CountryCode>()
for (const country of getCountries()) {
  const callingCode = `+${getCountryCallingCode(country)}`
  if (!countryByCallingCode.has(callingCode)) {
    countryByCallingCode.set(callingCode, country)
  }
}

/** Calling codes are one to four digits, so the longest match is the right one. */
const LONGEST_CALLING_CODE_LENGTH = 4

function findCallingCodeAtStart(digits: string): string | null {
  for (
    let codeLength = Math.min(LONGEST_CALLING_CODE_LENGTH, digits.length);
    codeLength > 0;
    codeLength -= 1
  ) {
    const candidateCode = `+${digits.slice(0, codeLength)}`
    if (countryByCallingCode.has(candidateCode)) {
      return candidateCode
    }
  }
  return null
}

/**
 * The number as it should read while somebody types it.
 *
 * A number typed with its own country code keeps it; one typed without gets the code this
 * deployment assumes, so the shape is settled from the first digit rather than changing
 * under the person halfway through. With no digits at all the field clears, because a
 * lone `+` is a door back to typing something else rather than a number.
 */
export function formatPhoneNumberAsTyped(
  rawPhoneNumber: string,
  defaultCallingCode: string | null = null,
): string {
  const trimmedPhoneNumber = rawPhoneNumber.trim()
  const typedDigits = trimmedPhoneNumber.replace(/[^0-9]/g, '')

  if (trimmedPhoneNumber.startsWith('+')) {
    const callingCode = findCallingCodeAtStart(typedDigits)
    // Still too few digits to know the country: show what was typed and wait.
    return callingCode === null
      ? `+${typedDigits}`
      : formatNationalNumber(callingCode, typedDigits.slice(callingCode.length - 1))
  }

  // An empty field stays empty. Putting the country code into it the moment it is cleared
  // would make it impossible to clear, and impossible to start a number for anywhere else.
  if (defaultCallingCode === null || typedDigits.length === 0) {
    return trimmedPhoneNumber
  }
  // A leading zero is the national trunk prefix, which an international number never has.
  return formatNationalNumber(defaultCallingCode, typedDigits.replace(/^0+/, ''))
}

function formatNationalNumber(callingCode: string, nationalDigits: string): string {
  if (nationalDigits.length === 0) {
    return callingCode
  }

  const formattingCountry = countryByCallingCode.get(callingCode)
  const nationalNumber =
    formattingCountry === undefined
      ? nationalDigits
      : new AsYouType(formattingCountry).input(nationalDigits)

  return `${callingCode} ${nationalNumber}`
}

/**
 * Where the caret belongs after reformatting, counted from the end.
 *
 * Counting from the front looks natural and is wrong: the mask inserts the country code
 * ahead of everything typed, so the caret would be pushed back by as many places as that
 * code has digits, and the next keystroke would land inside it. What does not move is how
 * many digits sit to the *right* of the caret, so that is what is followed.
 */
export function countDigitsAfter(text: string, caretPosition: number): number {
  return text.slice(caretPosition).replace(/[^0-9]/g, '').length
}

/**
 * Takes the digit a deletion should take when it landed on formatting instead: the
 * nearest digit before the caret going backward, after it going forward. Answers null
 * when there is no digit left to take on that side, so the field can empty.
 */
export function deleteDigitBeforeCaretPosition(
  text: string,
  caretPosition: number,
): { text: string; digitsAfterCaret: number } | null {
  for (
    let digitPosition = Math.min(caretPosition, text.length) - 1;
    digitPosition >= 0;
    digitPosition -= 1
  ) {
    if (/[0-9]/.test(text[digitPosition] as string)) {
      const reducedText = text.slice(0, digitPosition) + text.slice(digitPosition + 1)
      return { text: reducedText, digitsAfterCaret: countDigitsAfter(reducedText, digitPosition) }
    }
  }
  return null
}

export function deleteDigitAfterCaretPosition(
  text: string,
  caretPosition: number,
): { text: string; digitsAfterCaret: number } | null {
  for (
    let digitPosition = Math.min(caretPosition, text.length);
    digitPosition < text.length;
    digitPosition += 1
  ) {
    if (/[0-9]/.test(text[digitPosition] as string)) {
      const reducedText = text.slice(0, digitPosition) + text.slice(digitPosition + 1)
      return { text: reducedText, digitsAfterCaret: countDigitsAfter(reducedText, digitPosition) }
    }
  }
  return null
}

export function findCaretPositionBeforeTrailingDigits(
  formattedPhoneNumber: string,
  digitsAfterCaret: number,
): number {
  if (digitsAfterCaret <= 0) {
    return formattedPhoneNumber.length
  }

  let seenDigits = 0
  for (let position = formattedPhoneNumber.length - 1; position >= 0; position -= 1) {
    if (/[0-9]/.test(formattedPhoneNumber[position] as string)) {
      seenDigits += 1
      if (seenDigits === digitsAfterCaret) {
        return position
      }
    }
  }
  return 0
}

/**
 * A handle as it should read on screen: a phone number takes its own country's shape,
 * and anything else is returned untouched.
 *
 * Display only — canonical handles, stored values, links and copies keep E.164. Safe to
 * call on anything that might be a handle: an email address or a group name comes back
 * exactly as it went in, because only a valid number has a shape to take.
 */
export function formatHandleForDisplay(handle: string): string {
  const trimmedHandle = handle.trim()
  if (!trimmedHandle.startsWith('+')) {
    return handle
  }
  const typedDigits = trimmedHandle.replace(/[^0-9]/g, '')
  if (findCallingCodeAtStart(typedDigits) === null) {
    return handle
  }
  return formatPhoneNumberAsTyped(trimmedHandle)
}

/**
 * A handle as it is being typed into a box that takes an address or a number.
 *
 * The mask engages the moment the value can be a phone — a digit or `+` at the start —
 * and the installation country's shape opens at once; typing the country code switches
 * to that country's shape through the ordinary formatting. The moment anything else
 * appears the value passes through untouched, so an address is never corrupted, and
 * deleting back re-engages the mask by itself.
 */
export function formatHandleAsTyped(
  rawHandle: string,
  defaultCallingCode: string | null = null,
): string {
  if (!/^[+\d]/.test(rawHandle.trimStart())) {
    return rawHandle
  }
  if (/[^+\d\s().-]/.test(rawHandle)) {
    return rawHandle
  }
  return formatPhoneNumberAsTyped(rawHandle, defaultCallingCode)
}

/**
 * The phone example a placeholder shows for one assumed country code. The shapes come
 * from the same library as the mask, so the example always matches what typing does.
 */
const EXAMPLE_PHONE_NUMBER_BY_CALLING_CODE: Record<string, string> = {
  '+55': '+5511999991234',
  '+1': '+12025550123',
}

export function examplePhoneNumberForCallingCode(callingCode: string | null): string {
  if (callingCode !== null) {
    const examplePhoneNumber = EXAMPLE_PHONE_NUMBER_BY_CALLING_CODE[callingCode]
    if (examplePhoneNumber !== undefined) {
      return formatPhoneNumberAsTyped(examplePhoneNumber)
    }
  }
  return '+5511999991234'
}

/**
 * Drops the country code from a number that belongs to the country this deployment serves.
 *
 * `+55 (31) 99239-5142` is read by somebody in Brazil as `(31) 99239-5142`: the `+55` is
 * true of every number they will ever see here, and a list has no room for what it does not
 * distinguish. A number from anywhere else keeps its code, which is exactly what makes it
 * recognisable as being from anywhere else.
 *
 * Anything that is not a number in that country is returned untouched, so this is safe to
 * call on a name — somebody called `+55 anything` is not a phone number and is left alone.
 */
export function hideLocalCallingCode(
  displayName: string,
  defaultCallingCode: string | null,
): string {
  if (defaultCallingCode === null || !displayName.startsWith(defaultCallingCode)) {
    return displayName
  }

  const withoutCallingCode = displayName.slice(defaultCallingCode.length).trimStart()
  // A national number always starts with a digit or the bracket around its area code. This
  // is what keeps a name that merely begins with those characters from being cut.
  return /^[(\d]/.test(withoutCallingCode) ? withoutCallingCode : displayName
}
